<template>
    <CRow>
        <CDataTable
            class="col-12 px-0"
            :items="Object.values(media)"
            :fields="$dataTableFieldsMaker(fields)"
            hover
            :pagination="{ ...pagination, change: fetchMedia, limit: 10 }"
            addTableClasses="bg-white"
        >
            <template #id="{ index, item }">
                <td>
                    <FaIcon icon="star" class="cursor-pointer" @click="starIt(item)" :class="item.is_starred ? 'text-affili-golden-tainoi' : ''" />
                    {{ index + 1 }}
                </td>
            </template>
            <template #name="{ item }">
                <td>
                    <CLink class="px-1" :href="item.url" target="_blank">
                        <FaIcon icon="link" class="text-primary" />
                    </CLink>
                    {{ item.name }}
                </td>
            </template>
            <template #type="{ item }">
                <td class="text-center">{{ typeStr(item.type) }}</td>
            </template>

            <template #status="{ item }">
                <td class="small" v-if="$policy('media', item).change_status" :key="item.id + '-status-' + (item.skey ? item.skey : 0)">
                    <CSelect2
                        class="mb-0 px-0 py-1"
                        :options="mediaStatusOption"
                        v-model="item.status"
                        @update="(newVal, oldVal) => changeStatusMedia(item, newVal, oldVal)"
                        :addInputClasses="'mx-auto px-1 font-xs ' + (item.status_input_class ? item.status_input_class : mediaInputClass(item.status))"
                    />
                </td>
                <td v-else class="text-center large">
                    <span
                        :class="'badge badge-' + mediaStatusColor(item.status)"
                    >{{ mediaStatusStr(item.status) }}</span>
                </td>
            </template>

            <template #level="{ item }">
                <td class="small" v-if="$policy('media', item).change_level" :key="item.id + '-level-' + (item.key ? item.key : 0)">
                    <CSelect2
                        class="mb-0 px-0 py-1"
                        :options="mediaLevelOption"
                        v-model="item.level"
                        @update="(newVal, oldVal) => changeLevelMedia(item, newVal, oldVal)"
                        addInputClasses="mx-auto px-1 font-xs"
                    />
                </td>
                <td v-else class="text-center large">
                    <span>{{ mediaLevelStr(item.status) }}</span>
                </td>
            </template>

            <template #rejection_reason="{ item }">
                <td class="text-center">
                    <FaIcon v-if="item.rejection_reason" icon="eye" class="text-info" @click="rejectionReasonModal = {show: true, item: item}" />
                    <span v-else>---</span>
                </td>
            </template>

            <template #createdAt="{ item }">
                <td>{{ $moment(item.created_at).format('LL') }}</td>
            </template>

            <template #user="{ item }">
                <td>{{ item.user.full_name }}</td>
            </template>

            <template #actions="{ item }">
                <td>
                    <CLink class="px-1" :to="{ name: 'media.edit', params: { id: item.id } }">
                        <FaIcon icon="edit" />
                    </CLink>
                    <CLink class="px-1" @click="modal = { item: item, show: true }">
                        <FaIcon icon="trash" class="text-danger" />
                    </CLink>
                </td>
            </template>
        </CDataTable>
        <CModal :title="$t('media.delete')" color="danger" :show.sync="modal.show">
            {{ $t('media.are_you_sure_to_remove', { attribute: modal.item.name }) }}
            <template
                #footer
            >
                <CButton @click="modal.show = false" color="secondary">{{ $t('cancel') }}</CButton>
                <CButton @click="deleteMedia" color="danger">{{ $t('delete') }}</CButton>
            </template>
        </CModal>
        <CModal
            :v-if="changeStatusModal.show"
            :title="$t('media.media_rejection_reason')"
            color="danger"
            :show.sync="changeStatusModal.show"
        >
            {{ $t('media.write_the_reason_of_rejection', { attribute: changeStatusModal.item.name }) }}
            <CTextarea v-model="rejectionReason" class="mt-4" />
            <template #footer>
                <CButton
                    @click="changeStatusModal.show = false"
                    color="secondary"
                >{{ $t('cancel') }}</CButton>
                <CButton
                    :disabled="!rejectionReason"
                    @click="changeStatusMedia(changeStatusModal.item, MediaStatus.REJECTED, changeStatusModal.item.status, true)"
                    color="danger"
                >{{ $t('reject') }}</CButton>
            </template>
        </CModal>
        <CModal
            :v-if="changeLevelModal.show"
            :title="$t('media.change_level')"
            color="warning"
            :show.sync="changeLevelModal.show"
        >
            {{ $t('media.are_you_sure_to_change_level', { attribute: changeLevelModal.item.name }) }}
            <template #footer>
                <CButton
                    @click="changeLevelModal.show = false"
                    color="secondary"
                >{{ $t('cancel') }}</CButton>
                <CButton
                    @click="changeLevelMedia(changeLevelModal.item, changeLevelModal.newVal, changeLevelModal.item.level, true)"
                    color="warning"
                >{{ $t('accept') }}</CButton>
            </template>
        </CModal>
        <CModal
            :v-if="rejectionReasonModal.show"
            :show.sync="rejectionReasonModal.show"
        >
            <template #header-wrapper>
                <span></span>
            </template>
            <span>
                {{ rejectionReasonModal.item.rejection_reason }}
            </span>
            <template #footer-wrapper>
                <span></span>
            </template>
        </CModal>
    </CRow>
</template>

<script>
import fields from './fields'
import { MediaStatus } from '@/constants'
import { mediaService } from '@/services/api'
import { mapGetters, mapActions } from 'vuex'
import { mediaStatusOption, mediaLevelOption } from '@/common/data'
import { typeStr, mediaStatusStr, mediaStatusColor, mediaInputClass, mediaLevelStr } from './methods'

export default {
    name: 'Table',

    data() {
        return {
            fields,
            MediaStatus,
            mediaStatusOption,
            mediaLevelOption,
            modal: {
                show: false,
                item: {}
            },
            changeStatusModal: {
                show: false,
                item: {}
            },
            changeLevelModal: {
                show: false,
                item: {},
                newVal: ''
            },
            rejectionReason: null,
            rejectionReasonModal: {
                show: false,
                item: {}
            }
        }
    },

    computed: {
        ...mapGetters({
            media: 'media/media',
            pagination: 'media/pagination',
            filterMeFields: 'filterMeComponent/filterMeFields'
        }),
    },

    methods: {
        ...mapActions({
            fetch: 'media/fetch',
            changeStatus: 'media/changeStatus',
            changeLevel: 'media/changeLevel',
            delete: 'media/delete',
            setShowLoading: 'loadingComponent/setShowLoading'
        }),
        typeStr,
        mediaStatusStr,
        mediaStatusColor,
        mediaInputClass,
        mediaLevelStr,
        fetchMedia(activePage = 1) {
            this.setShowLoading(true)
            this.fetch({
                page: activePage,
                ...this.filterMeFields,
            }).then(() => this.setShowLoading(false))
        },
        deleteMedia() {
            this.delete(this.modal.item.id)
                .then(() => this.modal.show = false)
        },
        changeStatusMedia(medium, newVal, oldVal, confirmed = false) {
            if (newVal === MediaStatus.REJECTED && !confirmed) {
                medium.skey = (medium.skey ? medium.skey : 0) + 1
                medium.status = oldVal
                this.changeStatusModal = { item: medium, show: true }

                return false
            }

            this.changeStatus({
                id: medium.id,
                status: newVal,
                rejection_reason: this.rejectionReason
            }).then(() => {
                medium.status_input_class = this.mediaInputClass(newVal)
                this.rejectionReason = null
                this.changeStatusModal = { item: {}, show: false }
            })
        },
        changeLevelMedia(medium, newVal, oldVal, confirmed = false) {
            if (!confirmed) {
                medium.key = (medium.key ? medium.key : 0) + 1
                medium.level = oldVal
                this.changeLevelModal = { item: medium, show: true, newVal: newVal }

                return false
            }

            this.changeLevel({
                id: medium.id,
                level: newVal,
            }).then(() => {
                this.changeLevelModal = { item: {}, show: false, newVal: '' }
            })
        },
        starIt(item) {
            mediaService.changeStar(item.id, {
                is_starred: !item.is_starred
            }).then(res => {
                item.is_starred = !item.is_starred
            })
        }
    },
}
</script>